import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './MobileScaleStage.module.css';
import { bindMobileViewportSignals, createMobileViewportStabilizer } from '../../shared/utils/mobileViewportStability';

function parseCssPx(value) {
  const n = Number.parseFloat(String(value || '0'));
  return Number.isFinite(n) ? n : 0;
}

function getDevicePixelRatio() {
  if (typeof window === 'undefined') return 1;
  const raw = Number(window.devicePixelRatio || 1);
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  return Math.max(1, Math.min(4, raw));
}

function snapScaleToPhysicalPixelGrid(scale, baseWidth, baseHeight) {
  const safeScale = Number(scale);
  if (!Number.isFinite(safeScale) || safeScale <= 0) return 1;

  const dpr = getDevicePixelRatio();
  const safeBaseWidth = Math.max(1, Number(baseWidth) || 1);
  const safeBaseHeight = Math.max(1, Number(baseHeight) || 1);

  // [PATCH][ANCHOR:MOBILE_SCALE:PIXEL_GRID_SNAP]
  // 모바일 고해상도/고배율 화면에서 stage 확대 크기가 물리 픽셀 경계에 최대한 맞도록 미세 보정한다.
  const widthSnapped = Math.floor(safeScale * safeBaseWidth * dpr) / (safeBaseWidth * dpr);
  const heightSnapped = Math.floor(safeScale * safeBaseHeight * dpr) / (safeBaseHeight * dpr);
  const snapped = Math.min(widthSnapped, heightSnapped);

  return Number.isFinite(snapped) && snapped > 0 ? snapped : safeScale;
}

function supportsCssZoom() {
  if (typeof document !== 'undefined' && document.documentElement?.style) {
    return 'zoom' in document.documentElement.style;
  }
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') return true;
  return CSS.supports('zoom', '1');
}

/**
 * MobileScaleStage
 * - baseWidth/baseHeight 기준 고정 stage를 visualViewport에 contain 방식으로 맞춤
 * - safe-area padding은 wrapper에서 처리하고, stage는 CSS zoom으로 확대해 transform scale 블러를 줄임
 */
export default function MobileScaleStage({
  children,
  baseWidth = 375,
  baseHeight = 812,
  fit = 'contain', // 'contain' | 'width'
  minScale = 0.5,
  maxScale = Number.POSITIVE_INFINITY,
  verticalAlign = 'center', // 'top' | 'center' | 'bottom'
  // [ADD][ANCHOR:MOBILE_SCALE:VISIBLE_VIEWPORT_WIDTH_MODE]
  // 기본값은 기존 동작(max) 유지. 모바일 로비처럼 실제 브라우저 화면 중앙선이 중요한 화면만
  // visible 모드로 넘겨 visualViewport 기준 중앙정렬을 사용한다.
  viewportWidthMode = 'max', // 'max' | 'visible'
  // [ADD][2026-08-30][ANCHOR:MOBILE_SCALE:HORIZONTAL_FULL_BLEED_OPTION]
  // 로비처럼 화면 좌우 끝까지 정확히 채워야 하는 화면은 horizontal safe-area를
  // stage 축소에 사용하지 않는다. 상/하 safe-area는 기존대로 유지한다.
  horizontalFullBleed = false,
  // 로비처럼 상/하 safe-area를 내부 TopBar/BottomNav가 직접 처리하는 화면은
  // wrapper 단계에서 동일 inset을 다시 빼지 않는다.
  contentOwnsVerticalSafeArea = false,
  background = '#000',
}) {
  const viewportRef = useRef(null);
  const rafRef = useRef(null);
  const stabilizerRef = useRef(null);
  if (!stabilizerRef.current) {
    stabilizerRef.current = createMobileViewportStabilizer();
  }
  const [vp, setVp] = useState({
    w: 0,
    h: 0,
    availableW: 0,
    availableH: 0,
  });

  useEffect(() => {
    const readViewport = () => {
      const vv = window.visualViewport;
      const docEl = document.documentElement;
      // [PATCH][ANCHOR:MOBILE_SCALE:WIDTH_READ_MODE]
      // 기본 max 모드는 기존 화면들의 확대 기준을 유지한다.
      // 단, 모바일 로비는 iOS Chrome에서 documentElement 폭이 가상 stage 폭으로 커져
      // 실제 브라우저 화면 중앙선과 앱 중앙선이 어긋날 수 있으므로 visible 모드로 실제 보이는 폭을 우선한다.
      const visibleRawW =
        Number(vv?.width || 0) ||
        Number(window.innerWidth || 0) ||
        Number(docEl?.clientWidth || 0) ||
        Number(baseWidth || 0);
      const maxRawW = Math.max(
        Number(vv?.width || 0),
        Number(window.innerWidth || 0),
        Number(docEl?.clientWidth || 0),
        Number(baseWidth || 0)
      );
      const rawW = viewportWidthMode === 'visible' ? visibleRawW : maxRawW;
      const rawH = (vv?.height ?? window.innerHeight ?? docEl?.clientHeight ?? baseHeight) || baseHeight;
      const w = Math.max(1, Math.round(rawW + 0.01));
      const h = Math.max(1, Math.round(rawH + 0.01));

      let safeTop = 0;
      let safeBottom = 0;
      let safeLeft = 0;
      let safeRight = 0;

      try {
        const cs = window.getComputedStyle(viewportRef.current);
        safeTop = parseCssPx(cs.paddingTop);
        safeBottom = parseCssPx(cs.paddingBottom);
        safeLeft = parseCssPx(cs.paddingLeft);
        safeRight = parseCssPx(cs.paddingRight);
      } catch (_) {}

      const next = {
        w,
        h,
        // [FIX][2026-08-30][ANCHOR:MOBILE_SCALE:FULL_BLEED_AVAILABLE_WIDTH]
        // horizontalFullBleed 화면은 safe-area 때문에 전체 stage가 좌우로 축소되지 않게 한다.
        availableW: horizontalFullBleed
          ? w
          : Math.max(1, Math.round(w - safeLeft - safeRight)),
        availableH: Math.max(1, Math.round(h - safeTop - safeBottom)),
      };

      // [FIX][2026-08-30][ANCHOR:MOBILE_SCALE:UNIFIED_KEYBOARD_VIEWPORT_STABILITY]
      // 모든 모바일 입력 freeze 조건을 공용화하고, 키보드 dismiss 직후의 일시적인
      // 축소 visualViewport가 stage 높이를 덮어쓰지 않도록 recovery window를 둔다.
      return stabilizerRef.current.read(next);
    };

    const syncViewport = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const next = readViewport();
        setVp((prev) => {
          if (
            prev.w === next.w &&
            prev.h === next.h &&
            prev.availableW === next.availableW &&
            prev.availableH === next.availableH
          ) {
            return prev;
          }
          return next;
        });
      });
    };

    syncViewport();
    const t0 = setTimeout(syncViewport, 0);
    const t1 = setTimeout(syncViewport, 50);
    const t2 = setTimeout(syncViewport, 200);

    const unbindViewportSignals = bindMobileViewportSignals(syncViewport);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      unbindViewportSignals?.();
      stabilizerRef.current?.reset?.();
    };
  }, [baseWidth, baseHeight, viewportWidthMode, horizontalFullBleed, contentOwnsVerticalSafeArea]);

  const scale = useMemo(() => {
    const vw = vp.availableW || vp.w || baseWidth;
    const vh = vp.availableH || vp.h || baseHeight;
    const sW = vw / baseWidth;
    const sH = vh / baseHeight;
    const raw = fit === 'width' ? sW : Math.min(sW, sH);
    const upper = Number.isFinite(maxScale) ? maxScale : Number.POSITIVE_INFINITY;
    const clamped = Math.max(minScale, Math.min(upper, raw));

    // [FIX][2026-08-30][ANCHOR:MOBILE_SCALE:WIDTH_FIT_NO_UNDERSCAN]
    // width-fit 화면에서 scale을 Math.floor 기반 물리픽셀 snap으로 다시 낮추면
    // 일부 DPR에서 좌우에 1px 안팎의 검은 여백이 생길 수 있다.
    // width-fit은 계산된 폭을 그대로 사용해 viewport를 정확히 채운다.
    if (fit === 'width') {
      return Number.isFinite(clamped) && clamped > 0 ? clamped : 1;
    }

    const snapped = snapScaleToPhysicalPixelGrid(clamped, baseWidth, baseHeight);
    return Number.isFinite(snapped) && snapped > 0 ? snapped : 1;
  }, [vp.availableW, vp.availableH, vp.w, vp.h, baseWidth, baseHeight, fit, minScale, maxScale]);

  const scaledW = Math.ceil(baseWidth * scale);
  const scaledH = Math.ceil(baseHeight * scale);
  const canUseZoom = supportsCssZoom();
  const viewportAlignItems =
    verticalAlign === 'top'
      ? 'flex-start'
      : verticalAlign === 'bottom'
        ? 'flex-end'
        : 'center';

  const fullBleedWidth = Math.ceil((vp.availableW || vp.w || baseWidth) / scale);

  const viewportStyle = {
    '--app-viewport-width': vp.w ? `${vp.w}px` : undefined,
    '--app-viewport-height': vp.h ? `${vp.h}px` : undefined,
    alignItems: viewportAlignItems,
    background,
    // 좌우 full-bleed 로비에서는 wrapper padding 자체도 제거해 stage의 기준점을
    // visual viewport의 실제 좌/우 끝과 일치시킨다.
    paddingLeft: horizontalFullBleed ? 0 : undefined,
    paddingRight: horizontalFullBleed ? 0 : undefined,
    // [FIX][2026-08-30][ANCHOR:MOBILE_SCALE:CONTENT_OWNS_VERTICAL_SAFE_AREA]
    // 모바일 로비는 topbar/bottom-nav가 safe-area를 직접 포함하므로 wrapper의
    // 상/하 padding을 제거해 inset이 두 번 적용되는 것을 막는다.
    paddingTop: contentOwnsVerticalSafeArea ? 0 : undefined,
    paddingBottom: contentOwnsVerticalSafeArea ? 0 : undefined,
  };

  return (
    <div ref={viewportRef} className={styles.viewport} style={viewportStyle}>
      <div className={styles.scaledBox} style={{ width: scaledW, height: scaledH }}>
        <div
          className={styles.stage}
          style={{
            width: baseWidth,
            height: baseHeight,
            // [PATCH][ANCHOR:MOBILE_SCALE:ZOOM_NOT_TRANSFORM]
            // transform scale은 모바일에서도 텍스트/로고를 통째로 확대해 흐려질 수 있다.
            // 지원 브라우저에서는 zoom으로 다시 렌더링하고, 미지원 환경만 transform으로 폴백한다.
            zoom: canUseZoom ? scale : undefined,
            transform: canUseZoom ? undefined : `scale(${scale})`,
            // [PATCH][ANCHOR:MOBILE_SCALE:FULL_BLEED_WIDTH]
            // stage가 contain/zoom으로 중앙 정렬될 때, 내부 컴포넌트가 화면 전체 폭의 선을 그릴 수 있도록
            // 현재 스케일을 역산한 논리 px 값을 하위 CSS 변수로 내려준다.
            '--mobile-stage-fullbleed-w': `${fullBleedWidth}px`,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
